import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy, KeyRound, Trash2, Smartphone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

const ROLES = [
  { value: "editor", label: "Content Editor — main page, sectors, joint ventures, leadership" },
  { value: "admin", label: "Account Owner — full access" },
];

function Credentials({ invite }) {
  const studioUrl = `${window.location.origin}/studio`;
  const copy = () => {
    const text = `Al Qalaa Content Studio\n${studioUrl}\n\nEmail: ${invite.email}\nTemporary password: ${invite.temp_password}\n\nYou will be asked to choose your own password the first time you sign in.`;
    navigator.clipboard?.writeText(text);
    toast.success("Invitation copied — paste it to them");
  };

  return (
    <div
      className="mt-5 border border-primary/50 bg-primary/5 p-5"
      style={{ borderRadius: "var(--radius)" }}
      data-testid="team-invite-result"
    >
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">Send them this — shown once</p>
      <dl className="mt-3 space-y-1.5 text-sm">
        <div className="flex gap-2">
          <dt className="w-44 shrink-0 text-muted-foreground">Mobile studio</dt>
          <dd className="font-medium break-all">{studioUrl}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-44 shrink-0 text-muted-foreground">Email</dt>
          <dd className="font-medium">{invite.email}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-44 shrink-0 text-muted-foreground">Temporary password</dt>
          <dd className="font-mono font-medium text-primary" data-testid="team-temp-password">{invite.temp_password}</dd>
        </div>
      </dl>
      <button type="button" onClick={copy} data-testid="team-copy-invite" className="btn-gold mt-4 h-10 px-5 text-xs">
        <Copy className="me-2 inline h-3.5 w-3.5" /> Copy invitation
      </button>
    </div>
  );
}

export default function TeamTab() {
  const [members, setMembers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "editor" });
  const [invite, setInvite] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/team").then((r) => setMembers(r.data)).catch(() => setMembers([]));
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/team", form);
      setInvite(null);
      setForm({ name: "", email: "", role: "editor" });
      await load();
      setInvite(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (m) => {
    try {
      const { data } = await api.post(`/admin/team/${m.id}/reset-password`);
      setInvite(null);
      await load();
      setInvite(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email}? They lose access immediately.`)) return;
    try {
      await api.delete(`/admin/team/${m.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (members === null) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="team-tab">
      <div className="border border-border p-6 md:p-8" style={{ borderRadius: "var(--radius)" }}>
        <h3 className="font-head text-2xl">Invite someone</h3>
        <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
          <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          A Content Editor signs in at <span className="font-medium text-foreground">/studio</span> — a phone-friendly
          screen for the main page, sectors, joint ventures and leadership, with the AI writer built in. They cannot
          touch the template, SEO, offices, map pins or messages.
        </p>
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          <div>
            <Label className="text-xs text-muted-foreground">Their name</Label>
            <Input
              className="mt-1.5"
              value={form.name}
              placeholder="Full name"
              data-testid="team-name-input"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Their email</Label>
            <Input
              className="mt-1.5"
              type="email"
              value={form.email}
              placeholder="name@company.com"
              data-testid="team-email-input"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">What can they do?</Label>
            <select
              className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
              value={form.role}
              data-testid="team-role-select"
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>
        <button onClick={add} disabled={busy} data-testid="team-invite-btn" className="btn-gold mt-5 h-10 px-5 text-xs disabled:opacity-50">
          <UserPlus className="me-2 inline h-3.5 w-3.5" /> {busy ? "Creating…" : "Create their access"}
        </button>
        {invite && <Credentials key={invite.temp_password} invite={invite} />}
      </div>

      <div>
        <h3 className="mb-5 font-head text-2xl">Who has access</h3>
        <div className="space-y-3" data-testid="team-list">
          {members.map((m) => (
            <div
              key={m.id}
              data-testid={`team-member-${m.email}`}
              className="flex flex-wrap items-center justify-between gap-4 border border-border p-5"
              style={{ borderRadius: "var(--radius)" }}
            >
              <div>
                <p className="font-medium">{m.name || m.email}</p>
                <p className="text-xs text-muted-foreground">{m.email}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full border border-border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  {m.role === "admin" ? "Account Owner" : "Content Editor"}
                </span>
                {m.must_change_password && (
                  <span className="text-[11px] text-primary">Has not signed in yet</span>
                )}
                <button
                  onClick={() => reset(m)}
                  data-testid={`team-reset-${m.email}`}
                  className="text-xs text-muted-foreground hover:text-primary"
                >
                  <KeyRound className="me-1 inline h-3.5 w-3.5" /> New password
                </button>
                {m.role !== "admin" && (
                  <button
                    onClick={() => remove(m)}
                    data-testid={`team-remove-${m.email}`}
                    className="text-xs text-destructive hover:underline"
                  >
                    <Trash2 className="me-1 inline h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
